import { CheckCircle, Video } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/* ===== Bild-Quelle robust bestimmen (Base64 ODER Datei-Pfad) ===== */
function buildMenteeImageSrc(mentee) {
  if (!mentee) return null;

  // 1) Falls vom Backend Base64 geliefert wird
  if (typeof mentee.avatar === "string" && mentee.avatar.trim()) {
    // Bereits als data:-URL?
    if (/^data:image\//i.test(mentee.avatar)) return mentee.avatar;
    // Sonst als PNG Base64 verpacken
    return `data:image/png;base64,${mentee.avatar}`;
  }

  // 2) Datei-Pfad aus avatarPath / avatar_path
  const rawPath = (mentee.avatarPath ?? mentee.avatar_path ?? "")
    .toString()
    .replace(/\\/g, "/")
    .replace(/^uploads[\\/]+/, ""); // evtl. führendes "uploads/" entfernen

  if (rawPath) {
    // Dateien werden unter /mentoring/uploads/ ausgeliefert (Express/NGINX)
    return `${window.location.origin}/mentoring/uploads/${rawPath}`;
  }

  // 3) Kein Bild vorhanden
  return null;
}

export default function MenteeMatchInfo({
  mentee,
  onShowDetails,
  onSendMessage,
  onStartVideoCall,
  unreadCount
}) {
  const [showDetails, setShowDetails] = useState(false);
  const { t, i18n } = useTranslation();
  const isEn = (i18n.language || "de").toLowerCase().startsWith("en");
  const tx = (key, deFallback, enFallback) =>
    t(key, { defaultValue: isEn ? enFallback : deFallback });

  // Fallback für Matching-Score
  const menteeScore = mentee?.score || mentee?.gesamtScore || null;

  // Kurzbeschreibung + Motivation (robust: camelCase + snake_case)
  const profileDescription =
    mentee?.profileDescription ??
    mentee?.profile_description ??
    "";

  const whyMentoring =
    mentee?.whyMentoring ??
    mentee?.why_mentoring ??
    "";

  // Safety-Fix für Arrays – behandelt alle möglichen Formate (Array, String, undefined)
  let sharedGoals = [];
  let sharedCompetences = [];

  if (Array.isArray(mentee.shared_goals)) {
    sharedGoals = mentee.shared_goals;
  } else if (
    typeof mentee.shared_goals === "string" &&
    mentee.shared_goals.trim().length > 0
  ) {
    try {
      sharedGoals = JSON.parse(mentee.shared_goals);
    } catch {
      sharedGoals = [];
    }
  }

  if (Array.isArray(mentee.shared_competences)) {
    sharedCompetences = mentee.shared_competences;
  } else if (
    typeof mentee.shared_competences === "string" &&
    mentee.shared_competences.trim().length > 0
  ) {
    try {
      sharedCompetences = JSON.parse(mentee.shared_competences);
    } catch {
      sharedCompetences = [];
    }
  }

  // Bild-Quelle nach den obigen Regeln ermitteln
  const imgSrc =
    buildMenteeImageSrc(mentee) ||
    `${window.location.origin}/mentoring/uploads/default-avatar.png`;

  return (
    <div className="bg-gray-800 rounded-2xl shadow-xl px-8 py-8 min-h-[260px] flex flex-col items-center justify-center w-full">
      <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full">
        {/* Avatar und Score */}
        <div className="flex flex-col items-center min-w-[150px]">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <img
              src={imgSrc}
              alt="Mentee"
              className="w-20 h-20 rounded-full border-4 border-white object-cover shadow"
              style={{ background: "#181e26" }}
            />
            {/* Runde Score-Badge mittig-unten */}
            <span
              className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 px-3 py-1 text-xs font-bold bg-mentixxgreen text-black rounded-full border-2 border-white shadow"
              style={{ fontSize: "1rem" }}
            >
              {menteeScore ? `${Number(menteeScore).toFixed(1)}%` : "-"}
            </span>
          </div>

          <div className="text-base font-bold text-white mt-2 text-center">
            {mentee.first_name} {mentee.last_name}
          </div>
          <div className="text-sm text-gray-300 text-center">
            {mentee.job_title}
          </div>
          <div className="text-xs text-gray-400 text-center">
            {mentee.abteilung}
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-2 w-full mt-4">
            <button
              onClick={() => onSendMessage(mentee)}
              className="bg-mentixxgreen text-black font-semibold py-2 px-6 rounded-full shadow hover:bg-green-400 transition relative flex items-center justify-center gap-2"
            >
              {t("menteeMatchInfo.sendMessage")}
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              onClick={onStartVideoCall}
              className="bg-blue-700 text-white font-semibold py-2 px-6 rounded-full shadow hover:bg-blue-500 transition flex items-center justify-center gap-2"
            >
              <Video size={18} /> {t("menteeMatchInfo.startVideoCall")}
            </button>
          </div>
        </div>

        {/* Hauptinhalt */}
        <div className="flex flex-col items-center flex-1 gap-2">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-mentixxgreen" size={24} />
            <span className="font-semibold text-mentixxgreen text-lg">
              {t("menteeMatchInfo.perfectMatch")}
            </span>
          </div>

          <div className="text-xl text-white font-light text-center mb-1">
            {t("menteeMatchInfo.menteeActive")}
          </div>

          <div className="text-gray-400 text-sm mb-4 text-center">
            {t("menteeMatchInfo.experience")}:{" "}
            <span className="font-medium">{mentee.experience}</span>
          </div>

          <div className="flex flex-col md:flex-row w-full justify-center gap-4 mt-1">
            <button
              onClick={() => setShowDetails(true)}
              className="flex-1 py-2 border border-gray-500 text-gray-200 rounded-full text-base hover:bg-gray-700 transition"
            >
              {t("menteeMatchInfo.moreAboutMentee")}
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showDetails && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-60 flex items-center justify-center">
          <div className="bg-[#232931] rounded-2xl p-8 w-full max-w-lg shadow-2xl">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-mentixxgreen text-xl font-bold">
                {t("menteeMatchInfo.modalTitle")}
              </span>
              <button
                className="ml-auto text-gray-400 hover:text-white text-xl"
                onClick={() => setShowDetails(false)}
                aria-label={t("menteeMatchInfo.close")}
              >
                ×
              </button>
            </div>

            {/* ✅ Neu: sprachabhängige Labels statt Hardcode */}
            <div className="mb-4">
              <div className="text-base text-gray-200 font-semibold mb-1">
                {t("menteeMatchInfo.profileDescriptionLabel", "Short description")}
              </div>
              {profileDescription?.trim() ? (
                <div className="text-gray-300 text-sm whitespace-pre-wrap">
                  {profileDescription}
                </div>
              ) : (
                <span className="text-gray-400 text-sm">
                  {t("menteeMatchInfo.noProfileDescription", "No short description provided")}
                </span>
              )}
            </div>

            <div className="mb-4">
              <div className="text-base text-gray-200 font-semibold mb-1">
                {t("menteeMatchInfo.motivationLabel", "Motivation")}
              </div>
              {whyMentoring?.trim() ? (
                <div className="text-gray-300 text-sm whitespace-pre-wrap">
                  {whyMentoring}
                </div>
              ) : (
                <span className="text-gray-400 text-sm">
                  {t("menteeMatchInfo.noMotivation", "No motivation provided")}
                </span>
              )}
            </div>

            {mentee.surveyId === 28 ? (
              <>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("menteeMatchInfo.applicationFields", "Anwendungsfelder", "Fields of Application")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentee.applicationFields?.length ? (
                      mentee.applicationFields.map((f, i) => (
                        <span key={i} className="bg-green-700/70 text-green-200 px-3 py-1 rounded-full text-xs">
                          {f}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("menteeMatchInfo.methodsSkills", "Methoden & Skills", "Methods & Skills")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentee.methodsSkills?.length ? (
                      mentee.methodsSkills.map((s, i) => (
                        <span key={i} className="bg-blue-800/60 text-blue-200 px-3 py-1 rounded-full text-xs">
                          {s}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {t("menteeMatchInfo.sharedGoals")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sharedGoals.length > 0 ? (
                      sharedGoals.map((g, i) => (
                        <span key={i} className="bg-green-700/70 text-green-200 px-3 py-1 rounded-full text-xs">
                          {g}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">{t("menteeMatchInfo.noSharedGoals")}</span>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {t("menteeMatchInfo.sharedCompetences")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sharedCompetences.length > 0 ? (
                      sharedCompetences.map((c, i) => (
                        <span key={i} className="bg-blue-800/60 text-blue-200 px-3 py-1 rounded-full text-xs">
                          {c}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">{t("menteeMatchInfo.noSharedCompetences")}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
